import React, {
  useRef,
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  StyleSheet,
  PixelRatio,
  Pressable,
  View,
  Modal,
  TextInput,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import BottomSheet, {
  BottomSheetBackdropProps,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import ScreenContext from '../../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import Styles from 'src/components/Styles';
import Localized from 'src/constants/AppStrings';
import {Platform} from 'react-native';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import AVText from 'src/components/elements/AVText';
import {handleBackdropPressHelper} from 'src/components/screens/rewards/policyHelper';
import CustomToggleComponent, {
  CustomToggleComponentType,
} from 'src/components/elements/CustomToggleComponent';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import Util, {rewardsStartPolicyInBackground} from 'src/Util';
import {getDescriber} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import Settings from 'src/Settings';
import {withGlobalize, WithGlobalizeProps} from 'src/i18n/HOC/withGlobalize';
import ActionsFactory from 'src/actions/ActionsFactory';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import useKeyboard from 'src/components/screens/rewards/phoneNumber/KeyboardHandler';
import AccountStore from 'src/stores/AccountStore';
import KeyboardAvoidingView from '../../../elements/365KeyboardAvoidingView';

export type AddPhoneNumberScreenProps = WithGlobalizeProps & {
  rewardPolicyId?: string;
  fromRewards: boolean;
};

enum BottomSheetState {
  NOT_READY = 'NOT_READY',
  FULLY_OPEN = 'FULLY_OPEN',
  FULLY_CLOSED = 'FULLY_CLOSED',
}

const AddPhoneNumber: React.FC<AddPhoneNumberScreenProps> = (props) => {
  const context = useContext(ScreenContext);
  const {getPickupLocationStyles, getActiveButtonColor} = getDescriber();

  const bottomSheetRef = useRef<BottomSheet>(null);
  const [onCloseCalled, setOnCloseCalled] = useState(false);
  const [bottomSheetCurrentState, setBottomSheetCurrentState] =
    useState<BottomSheetState>(BottomSheetState.NOT_READY);
  const [modalVisible, setModalVisible] = useState(Platform.OS === 'web');

  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const keyBoardHeight = useKeyboard();

  const countryCode = '+1';

  const bottomSheetCustomStates = useMemo(
    () => ({
      [-1]: BottomSheetState.FULLY_CLOSED,
      0: BottomSheetState.FULLY_OPEN,
      999: BottomSheetState.NOT_READY,
    }),
    [],
  );

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'AddPhoneNumber', {
      ...props,
    });
    if (bottomSheetRef.current) {
      bottomSheetRef.current.close();
    }
  }, [props]);

  const handleNext = useCallback(async () => {
    context.actions.showSpinner();
    try {
      const phone = countryCode + phoneNumber.replaceAll('-', '');
      const res = await ActionsFactory.getAccountActions().sendSmsCodeAction(
        phone,
      );

      if (!res.hasOwnProperty('statusCode')) {
        props.fromRewards &&
          (await rewardsStartPolicyInBackground(
            AccountStore.getAccountId(),
            props.rewardPolicyId,
            AppRoutes.AddPhoneNumber,
          ));
        onClose();
        NavActions.navigate(AppRoutes.VerifyPhoneNumber, {
          phoneNumber: phoneNumber,
          countryCode: countryCode,
          fromRewards: props.fromRewards,
          rewardPolicyId: props.rewardPolicyId,
        });
      }
    } catch (err) {
      FirebaseAnalytic.trackEvent('handleNext: Error', 'AddPhoneNumber', {
        error: err,
      });
    } finally {
      context.actions.hideSpinner();
    }
  }, [phoneNumber]);

  const onClose = useCallback(() => {
    if (onCloseCalled) {
      return;
    }
    setOnCloseCalled(true);

    FirebaseAnalytic.trackEvent('onClose', 'AddPhoneNumber', {
      ...props,
      navigate: AppRoutes.RewardsScreen,
    });

    if (Platform.OS === 'web') {
      setModalVisible(false);
    } else if (bottomSheetRef.current) {
      bottomSheetRef.current.forceClose();
    }

    NavActions.pop();
  }, [onCloseCalled, props]);

  const handleSheetChange = (index: number) => {
    setBottomSheetCurrentState(bottomSheetCustomStates[index]);
    if (index === -1) {
      onClose();
    }
  };

  const handleBackdropPress = () => {
    if (bottomSheetCurrentState === BottomSheetState.NOT_READY) {
      NavActions.pop();
    } else if (bottomSheetRef.current) {
      bottomSheetRef.current.forceClose();
    }
  };

  const handlePress = React.useCallback(() => {
    handleBackdropPressHelper({onClose, bottomSheetRef});
  }, [onClose]);

  const renderBackdropComponent = (backDropProps: BottomSheetBackdropProps) => (
    <Pressable style={backDropProps.style} onPress={handleBackdropPress} />
  );

  const onPhoneNumberChange = (value: string) => {
    const formatedPhoneNumber = Util.formatPhoneNumber(value);
    setPhoneNumber(formatedPhoneNumber);
    setIsValidPhoneNumber(formatedPhoneNumber.length === 12);
  };

  const renderContent = useMemo(
    () => (
      <KeyboardAvoidingView behavior="height" additionalKeyboardHeight={50}>
        <View style={styles.content}>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
            style={styles.headerText}
          >
            {props.fromRewards
              ? Localized.Labels.add_your_phone_number_to_earn_reward_points
              : Localized.Labels.add_your_phone_number}
          </AVText>
          <View style={styles.phoneNumberView}>
            <View style={styles.countryCodeView}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                style={styles.countryCodeText}
              >
                {countryCode}
              </AVText>
            </View>
            <View style={styles.phoneNumberSeparator} />
            <TextInput
              style={styles.phoneNumberTextInput}
              placeholder={Localized.Labels.enter_your_phone_number}
              cursorColor={Styles.Colors.PayNew.darkHuesBase04}
              keyboardType="number-pad"
              onChangeText={(text) => onPhoneNumberChange(text)}
              value={phoneNumber}
              maxLength={12}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            />
          </View>
          <View style={styles.privacyView}>
            <CustomToggleComponent
              accessibilityLabel={
                Localized.Labels.add_your_phone_number_privacy_policy
              }
              selected={isPolicyAccepted}
              type={CustomToggleComponentType.Checkbox}
              onPress={() => setIsPolicyAccepted(!isPolicyAccepted)}
              testID="qa_Phone_checkbox"
            />
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              style={styles.privacyPolicyText}
            >
              {Localized.Labels.add_your_phone_number_privacy_policy}
            </AVText>
          </View>
        </View>
        <View
          style={Settings.isRefiveAnd365PayOnly() && styles.buttonParentView}
        >
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Solid}
            buttonViewStyle={[
              getPickupLocationStyles().buttonView,
              {
                backgroundColor: getActiveButtonColor(),
              },
            ]}
            inActiveButtonViewStyle={[
              getPickupLocationStyles().buttonView,
              ,
              styles.inActiveButtonView,
            ]}
            buttonContainerStyle={[
              getPickupLocationStyles().containerStyle,
              Settings.isRefiveAnd365PayOnly() && styles.buttonContainer,
            ]}
            accessibilityLabelValue={Localized.Buttons.next}
            onPress={handleNext}
            titleText={Localized.Buttons.next}
            accessibilityHintValue={'button'}
            aria-label={Localized.Buttons.next}
            isDisabled={!(isPolicyAccepted && isValidPhoneNumber)}
            testID="qa_btn_Next"
            textStyle={styles.buttonText}
            inActiveTextStyle={styles.buttonText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
          />
        </View>
      </KeyboardAvoidingView>
    ),
    [phoneNumber, isPolicyAccepted, isValidPhoneNumber],
  );

  const renderBottomSheet = () => (
    <BottomSheet
      ref={bottomSheetRef}
      snapPoints={[360 + PixelRatio.getFontScale() * 35 + keyBoardHeight]}
      enableHandlePanningGesture={true}
      onChange={handleSheetChange}
      enablePanDownToClose={true}
      backdropComponent={renderBackdropComponent}
      style={styles.bottomSheet}
    >
      <BottomSheetScrollView>{renderContent}</BottomSheetScrollView>
    </BottomSheet>
  );

  const renderModal = () => (
    <Modal
      animationType="fade"
      transparent
      visible={modalVisible}
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalContainer}>
          <AVTouchableOpacity
            aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} add phone number details`}
            role="button"
            style={styles.closeModalContainer}
            onPress={onClose}
            accessible
            accessibilityLabel={Localized.Buttons.cancel}
            accessibilityRole="button"
            hitSlop={Styles.HitSlop_10}
          >
            <View style={styles.closeModalTitleContainer}>
              <CancelIcon />
            </View>
          </AVTouchableOpacity>
          {renderContent}
        </View>
      </View>
    </Modal>
  );

  return (
    <View style={styles.container}>
      <Pressable style={styles.backdropPressable} onPress={handlePress} />
      {Platform.OS === 'web' ? renderModal() : renderBottomSheet()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Styles.bottomTreyBackgroundColor,
  },
  backdropPressable: {
    width: '100%',
    minHeight: '60%',
    backgroundColor: 'transparent',
  },
  bottomSheet: {
    borderRadius: Styles.Spacing.m4,
    overflow: 'hidden',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Styles.Spacing.m3,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m3 + Styles.Spacing.m2,
    overflow: 'hidden',
    padding: Styles.Spacing.m3 + Styles.Spacing.m2,
    paddingBottom: Styles.Spacing.m5,
  },
  closeModalContainer: {
    position: 'absolute',
    top: Styles.Spacing.m1,
    right: Styles.Spacing.m1,
    width: Styles.Heights.touchTargetHeight2,
    height: Styles.Heights.touchTargetHeight2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeModalTitleContainer: {
    width: Styles.Spacing.m2 + Styles.Spacing.m3,
    height: Styles.Spacing.m2 + Styles.Spacing.m3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    marginHorizontal: Styles.Spacing.m4 - Styles.Spacing.m2,
    marginVertical: Styles.Spacing.m4 - Styles.Spacing.m2,
  },
  headerText: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '700',
    color: Styles.black,
    textAlign: 'left',
  },
  privacyView: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
    marginHorizontal: Styles.Spacing.m0,
  },
  privacyPolicyText: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '400',
    color: Styles.Colors.PayNew.black01,
    textAlign: 'left',
    marginLeft: Styles.Spacing.m2,
    marginRight: Styles.Spacing.m4,
    marginTop: -Styles.Spacing.m1,
  },
  phoneNumberView: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 50,
    borderRadius: Styles.Spacing.m2,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase09,
    marginTop: Styles.Spacing.m3,
  },
  countryCodeView: {
    width: 60,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  countryCodeText: {
    textAlign: 'center',
    color: Styles.Colors.PayNew.darkHuesBase04,
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
    fontWeight: '400',
    marginRight: Styles.Spacing.m1,
  },
  phoneNumberSeparator: {
    height: 44,
    width: 1,
    backgroundColor: Styles.Colors.PayNew.neutralHuesBase09,
  },
  phoneNumberTextInput: {
    color: Styles.Colors.PayNew.black01,
    textAlign: 'left',
    paddingHorizontal: Styles.Spacing.m2,
    backgroundColor: Styles.transparent,
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
    fontWeight: '400',
  },
  buttonText: {
    fontSize: getLayoutDescriber().bodyFontSize(),
  },
  buttonParentView: {
    bottom: -Styles.Spacing.m4,
    right: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  inActiveButtonView: {
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
  },
  buttonContainer: {
    marginTop: Styles.Spacing.m0,
    alignSelf: 'flex-end',
    ...Platform.select({
      android: {
        bottom: Styles.Spacing.m4,
      },
      ios: {
        bottom: Styles.Spacing.m4,
      },
    }),
  },
});

export default withForwardedNavigationParams()(withGlobalize(AddPhoneNumber));
